import { Check, Minus } from 'lucide-react';
import {
  Checkbox as RC,
  RadioGroup as RR,
  Switch as RSw,
  Tabs as RTabs,
  ToggleGroup as RTG,
} from 'radix-ui';
import { useId, type ReactNode } from 'react';
import { cx } from '../lib/cx.js';
import './controls.css';

/* ---------- Switch ---------- */

export interface SwitchProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /** Visible label. Omit only when `aria-label` is given. */
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  'aria-label'?: string;
  className?: string;
}

export function Switch({ label, description, className, ...props }: SwitchProps) {
  const id = useId();
  const control = (
    <RSw.Root
      id={id}
      className="vel-switch"
      {...(props.checked !== undefined ? { checked: props.checked } : {})}
      {...(props.defaultChecked !== undefined ? { defaultChecked: props.defaultChecked } : {})}
      {...(props.onCheckedChange ? { onCheckedChange: props.onCheckedChange } : {})}
      {...(props.disabled !== undefined ? { disabled: props.disabled } : {})}
      aria-label={props['aria-label']}
      aria-describedby={description ? `${id}-d` : undefined}
    >
      <RSw.Thumb className="vel-switch__thumb" />
    </RSw.Root>
  );
  if (!label) return control;
  return (
    <div className={cx('vel-check-row', className)}>
      {control}
      <span className="vel-check-row__text">
        <label htmlFor={id}>{label}</label>
        {description ? (
          <span id={`${id}-d`} className="vel-check-row__desc">
            {description}
          </span>
        ) : null}
      </span>
    </div>
  );
}

/* ---------- Checkbox ---------- */

export interface CheckboxProps {
  checked?: boolean | 'indeterminate';
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean | 'indeterminate') => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  'aria-label'?: string;
  className?: string;
}

export function Checkbox({ label, description, className, ...props }: CheckboxProps) {
  const id = useId();
  const control = (
    <RC.Root
      id={id}
      className="vel-checkbox"
      {...(props.checked !== undefined ? { checked: props.checked } : {})}
      {...(props.defaultChecked !== undefined ? { defaultChecked: props.defaultChecked } : {})}
      {...(props.onCheckedChange ? { onCheckedChange: props.onCheckedChange } : {})}
      {...(props.disabled !== undefined ? { disabled: props.disabled } : {})}
      aria-label={props['aria-label']}
      aria-describedby={description ? `${id}-d` : undefined}
    >
      <RC.Indicator className="vel-checkbox__indicator">
        {props.checked === 'indeterminate' ? (
          <Minus size={12} strokeWidth={2.5} aria-hidden />
        ) : (
          <Check size={12} strokeWidth={2.5} aria-hidden />
        )}
      </RC.Indicator>
    </RC.Root>
  );
  if (!label) return control;
  return (
    <div className={cx('vel-check-row', className)}>
      {control}
      <span className="vel-check-row__text">
        <label htmlFor={id}>{label}</label>
        {description ? (
          <span id={`${id}-d`} className="vel-check-row__desc">
            {description}
          </span>
        ) : null}
      </span>
    </div>
  );
}

/* ---------- Radio ---------- */

export interface RadioOption {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps {
  options: readonly RadioOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  orientation?: 'vertical' | 'horizontal';
  'aria-label'?: string;
  className?: string;
}

export function RadioGroup({
  options,
  orientation = 'vertical',
  className,
  ...props
}: RadioGroupProps) {
  const id = useId();
  return (
    <RR.Root
      className={cx('vel-radio-group', className)}
      data-orientation={orientation}
      orientation={orientation}
      {...(props.value !== undefined ? { value: props.value } : {})}
      {...(props.defaultValue !== undefined ? { defaultValue: props.defaultValue } : {})}
      {...(props.onValueChange ? { onValueChange: props.onValueChange } : {})}
      aria-label={props['aria-label']}
    >
      {options.map((o) => (
        <div key={o.value} className="vel-check-row">
          <RR.Item
            id={`${id}-${o.value}`}
            value={o.value}
            disabled={o.disabled ?? false}
            className="vel-radio"
          >
            <RR.Indicator className="vel-radio__dot" />
          </RR.Item>
          <span className="vel-check-row__text">
            <label htmlFor={`${id}-${o.value}`}>{o.label}</label>
            {o.description ? <span className="vel-check-row__desc">{o.description}</span> : null}
          </span>
        </div>
      ))}
    </RR.Root>
  );
}

/* ---------- SegmentedControl ---------- */

export interface SegmentOption {
  value: string;
  label: ReactNode;
  icon?: ReactNode;
  /** Required when `label` is icon-only. */
  'aria-label'?: string;
  disabled?: boolean;
}

export interface SegmentedControlProps {
  options: readonly SegmentOption[];
  value: string;
  onValueChange: (value: string) => void;
  size?: 'sm' | 'md';
  'aria-label': string;
  className?: string;
}

/** Single-choice toggle strip, e.g. Day | Week | Month on the schedule toolbar. */
export function SegmentedControl({
  options,
  value,
  onValueChange,
  size = 'md',
  className,
  ...aria
}: SegmentedControlProps) {
  return (
    <RTG.Root
      type="single"
      className={cx('vel-segmented', className)}
      data-size={size}
      value={value}
      onValueChange={(v: string) => {
        // Radix allows deselecting; a segmented control always has one value.
        if (v) onValueChange(v);
      }}
      aria-label={aria['aria-label']}
    >
      {options.map((o) => (
        <RTG.Item
          key={o.value}
          value={o.value}
          disabled={o.disabled ?? false}
          aria-label={o['aria-label']}
          className="vel-segmented__item"
        >
          {o.icon ? (
            <span className="vel-segmented__icon" aria-hidden>
              {o.icon}
            </span>
          ) : null}
          {o.label}
        </RTG.Item>
      ))}
    </RTG.Root>
  );
}

/* ---------- Tabs ---------- */

export interface TabItem {
  value: string;
  label: ReactNode;
  /** Count badge after the label. */
  count?: number;
  content: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: readonly TabItem[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  'aria-label'?: string;
  className?: string;
}

export function Tabs({ items, value, defaultValue, onValueChange, className, ...aria }: TabsProps) {
  return (
    <RTabs.Root
      className={cx('vel-tabs', className)}
      {...(value !== undefined ? { value } : {})}
      {...((defaultValue ?? items[0]?.value)
        ? { defaultValue: (defaultValue ?? items[0]?.value) as string }
        : {})}
      {...(onValueChange ? { onValueChange } : {})}
    >
      <RTabs.List className="vel-tabs__list" aria-label={aria['aria-label']}>
        {items.map((t) => (
          <RTabs.Trigger
            key={t.value}
            value={t.value}
            disabled={t.disabled ?? false}
            className="vel-tabs__trigger"
          >
            {t.label}
            {t.count !== undefined ? <span className="vel-tabs__count">{t.count}</span> : null}
          </RTabs.Trigger>
        ))}
      </RTabs.List>
      {items.map((t) => (
        <RTabs.Content key={t.value} value={t.value} className="vel-tabs__panel">
          {t.content}
        </RTabs.Content>
      ))}
    </RTabs.Root>
  );
}
